import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { generatePath } from 'react-router';

import {
  AddressDto,
  BusinessCustomerDto,
  CreateBusinessCustomerRequestDto,
  CustomerPropertiesDto,
  CustomerTypeDto,
} from '@/api/generated';
import { ViewWrapper } from '@/components/viewWrapper/ViewWrapper.comp.tsx';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { flagOptions } from '@/utils/flagOptions.util.ts';
import { formatDateToBackend } from '@/utils/formatDate.util.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CustomersBusinessCreateEditForm } from '@/views/customers/createBusiness/components/CustomersBusinessCreateEditForm.comp.tsx';
import { CreateBusinessCustomerFormModel } from '@/views/customers/createBusiness/model/CreateBusinessCustomerForm.model.ts';
import { useCreateBusinessCustomer } from '@/views/customers/createBusiness/util/useCreateBusinessCustomer.util.ts';
import { useCreateBusinessCustomerFormValidation } from '@/views/customers/createBusiness/util/useCreateBusinessCustomerForm.validation.ts';
import { useUpdateBusinessCustomer } from '@/views/customers/createBusiness/util/useUpdateBusinessCustomer.util.ts';
import { customerFormSubmitUtil } from '@/views/customers/status/customerFormSubmit.util.ts';
import { useCustomerStatusField } from '@/views/customers/status/useCustomerStatusField.util.ts';

type Props = {
  customerDetails?: BusinessCustomerDto;
  onCancel?: () => void;
  onPostCreateAction?: (customer: CustomerPropertiesDto) => void;
};

const CustomersCreateBusinessView: React.FC<Props> = ({
  customerDetails,
  onCancel,
  onPostCreateAction,
}) => {
  const validation = useCreateBusinessCustomerFormValidation();
  const { createBusinessCustomer, isCreatingBusinessCustomer } = useCreateBusinessCustomer();
  const { isUpdatingBusinessCustomer, updateBusinessCustomer } = useUpdateBusinessCustomer();
  const { t } = useTranslationWithPrefix('views.customersBusinessCreate');
  const { t: dictT } = useTranslationWithPrefix('utils.dictionaries');
  const { push } = useAppNavigation();
  const statusField = useCustomerStatusField(customerDetails?.status);

  const formMethods = useForm<CreateBusinessCustomerFormModel>({
    defaultValues: customerDetails
      ? {
          ...customerDetails,
          ...customerDetails.address,
          flag: flagOptions(dictT).find(it => it.value === customerDetails.flag),
          foundingDate: customerDetails.foundingDate ? dayjs(customerDetails.foundingDate) : null,
          status: statusField.defaultOption,
        }
      : { status: statusField.defaultOption },
    resolver: yupResolver(validation),
  });

  const handleSubmit: SubmitHandler<CreateBusinessCustomerFormModel> = async data => {
    const { flag, foundingDate, name, nip, regon, status, ...address } = data;
    const dto: CreateBusinessCustomerRequestDto = {
      address: address as Partial<AddressDto>,
      flag: data.flag.value,
      foundingDate: formatDateToBackend(foundingDate),
      name,
      nip,
      regon,
      status: status?.value,
    };
    const openDetails = (customerId: number) =>
      push(generatePath(RouteEnum.CUSTOMERS_DETAILS, { customerId: customerId.toString() }));
    if (!customerDetails) {
      await customerFormSubmitUtil.create({
        create: () => createBusinessCustomer(dto),
        dto,
        onPostCreateAction,
        openDetails,
        type: CustomerTypeDto.Business,
      });
    } else {
      await customerFormSubmitUtil.update({
        customerId: customerDetails.customerId,
        openDetails,
        update: () =>
          updateBusinessCustomer({
            customerId: customerDetails.customerId,
            data: {
              ...dto,
              customerId: customerDetails.customerId,
              status: dto.status ?? customerDetails.status,
              type: customerDetails.type,
            },
          }),
      });
    }
  };

  const disabled = isCreatingBusinessCustomer || isUpdatingBusinessCustomer;
  return (
    <ViewWrapper sx={{ px: 5, py: 3 }}>
      <CustomersBusinessCreateEditForm
        disabled={disabled}
        formMethods={formMethods}
        isEdit={Boolean(customerDetails)}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        statusField={statusField}
        t={t}
        validation={validation}
      />
    </ViewWrapper>
  );
};

export default CustomersCreateBusinessView;
